import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';

type Tone = 'default' | 'warning';

/**
 * Relays the row axis of a subgrid through an intermediate wrapper (a `<li>`, typically). Carries the
 * same `rowGap` as the shell it wraps: two subgrids covering the same tracks with different `row-gap`
 * values fall out of alignment.
 */
const relayStyle = (trackCount: number, rowGap: string | undefined) => css`
	display: grid;
	grid-row: span ${trackCount};
	grid-template-rows: subgrid;
	${rowGap !== undefined ? `row-gap: ${rowGap};` : ''}
`;

const subgridShellStyle = (
	trackCount: number,
	tone: Tone,
	dimmed: boolean,
	rowGap: string | undefined,
) => css`
	display: grid;
	grid-template-rows: subgrid;
	grid-row: span ${trackCount};
	${rowGap !== undefined ? `row-gap: ${rowGap};` : ''}
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${tone === 'warning' ? theme.states.warning.ink : theme.colors.border};
	opacity: ${dimmed ? 0.6 : 1};
`;

/**
 * A card with no parent grid to align with: `subgrid`/`grid-row` would be inert here (`subgrid` falls
 * back to `none` without a grid ancestor, `grid-row` is ignored inside a flex parent), so children stack
 * in a plain column instead.
 */
const stackedShellStyle = (tone: Tone, dimmed: boolean, rowGap: string | undefined) => css`
	display: flex;
	flex-direction: column;
	${rowGap !== undefined ? `row-gap: ${rowGap};` : ''}
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${tone === 'warning' ? theme.states.warning.ink : theme.colors.border};
	opacity: ${dimmed ? 0.6 : 1};
`;

type SharedProps = {
	/** Hairline color. `warning` flags a card that needs attention; `default` is the resting state. */
	readonly tone?: Tone;
	/** Dims the card (a disabled account, for instance). */
	readonly dimmed?: boolean;
	/**
	 * Forwarded to the `<article>` shell — a caller writes `<GridCard css={…}>`, and the Emotion `css`
	 * prop resolves to this `className`. Reserved for what belongs to the card's own box and nothing a
	 * child could carry instead: a positioning context for a full-card overlay link, `:hover`, or
	 * `:focus-within`/`:has(:focus-visible)`. Never for redeclaring the shell itself — padding, radius,
	 * surface, hairline stay `GridCard`'s property, and a caller who overrides one of them here does so
	 * silently: the passed `css` merges into the same rule as the shell's own and wins on any property
	 * it repeats, so a mistaken override produces no error, no lint diagnostic, and no failing test.
	 */
	readonly className?: string;
	readonly children: React.ReactNode;
};

/**
 * The layout every card in a grid of cards uses: the shell aligned on a `trackCount`-track subgrid so
 * sibling cards sharing a visual grid row settle on the same height, block for block. `layout` omitted
 * defaults to this branch.
 */
type SubgridProps = SharedProps & {
	readonly layout?: 'subgrid';
	/**
	 * Only meaningful in `subgrid` layout. Number of direct grid children this card emits — fixed,
	 * identical on every render branch. A branch with nothing to say in a track still renders an empty
	 * `<div />`: skipping a track shifts every following one, silently — no compile error, no Biome
	 * rule, no red test catches it.
	 */
	readonly trackCount: number;
	/**
	 * Only meaningful in `subgrid` layout. Wraps the card in a `<li>` that itself relays `display: grid;
	 * grid-row: span; grid-template-rows: subgrid`. Any intermediate node between the parent grid and
	 * this card breaks the subgrid chain unless it forwards the row axis the same way — a bare `<li>`
	 * would silently misalign the card.
	 */
	readonly asListItem?: boolean;
	/**
	 * Gap between the card's direct children, applied to the whole subgrid chain — the shell and, with
	 * `asListItem`, its `<li>` relay — so every level describes the same spacing. Omitted, the card
	 * inherits its parent grid's gap: a reasonable default, since a subgrid card is always read inside a
	 * grid that already sets one.
	 */
	readonly rowGap?: string;
};

/**
 * A standalone card, never rendered next to a sibling on a shared grid row — `layout="stacked"` is
 * required precisely because there is no default to fall back on: unlike `subgrid`, this branch cannot
 * be inferred from an omitted prop.
 */
type StackedProps = SharedProps & {
	readonly layout: 'stacked';
	/**
	 * Column gap between the card's direct children. Required: a stacked card has no parent grid to
	 * inherit spacing from, so an omitted value would leave children touching with no gap at all.
	 */
	readonly rowGap: string;
};

type Props = SubgridProps | StackedProps;

/**
 * Shared card shell — fixed padding, radius and hairline border — in one of two layouts. `subgrid`
 * (default) aligns the card with its siblings on the page grid; every caller must then emit exactly
 * `trackCount` direct children, in the same order, on every render branch (see `trackCount` above for
 * the cost of missing that). `stacked` is the shell alone, for a card with no grid to align with.
 */
const GridCard: React.FC<Props> = (props) => {
	const { tone = 'default', dimmed = false, rowGap, className, children } = props;
	/**
	 * `undefined` exactly when `props.layout === 'stacked'`: a subgrid card always carries a
	 * `trackCount`, a stacked one never does. Checking this primitive's definedness below is therefore
	 * enough to pick the right shell, and TypeScript narrows it without a cast.
	 */
	const trackCount = props.layout === 'stacked' ? undefined : props.trackCount;

	const style = React.useMemo(() => {
		if (trackCount === undefined) {
			return stackedShellStyle(tone, dimmed, rowGap);
		}

		return subgridShellStyle(trackCount, tone, dimmed, rowGap);
	}, [trackCount, tone, dimmed, rowGap]);

	const article = (
		<article css={style} className={className}>
			{children}
		</article>
	);

	if (props.layout === 'stacked') {
		return article;
	}

	if (props.asListItem) {
		return <li css={relayStyle(props.trackCount, rowGap)}>{article}</li>;
	}

	return article;
};

export default GridCard;
